import { useEffect, useState } from 'react';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from 'react-toastify';
import toastConfig from '../../config/Toast';
import useApi from '../../api';
import { Loader2, Mic, Play, Volume2, Lock } from 'lucide-react';
import { VoiceOption, VoiceTabProps } from './types';
import { useUser } from '../../user/UserContext';
import Upgrade from '../../modals/Upgrade';
import useAnalytics from '../../analytics/analytics';
import AgentTabHeader from './AgentTabHeader';

const FALLBACK_PREVIEW_TEXT = 'Hi! How are you? How can I help you today?';

export default function VoiceTab({
  agId,
  agentName: _agentName,
  voiceEnabled,
  setVoiceEnabled,
  selectedVoice,
  setSelectedVoice,
  setNeedsUpdate,
}: VoiceTabProps) {
  const { get, postStream } = useApi();
  const { checkLicenseType } = useUser();
  const hasBusinessPlan = checkLicenseType('business');
  const { captureEvent } = useAnalytics();

  const [voices, setVoices] = useState<VoiceOption[]>([]);
  const [loadingVoices, setLoadingVoices] = useState(false);
  const [previewing, setPreviewing] = useState<string | null>(null);
  const [upgradeModalVisible, setUpgradeModalVisible] = useState(false);

  const fetchVoices = async () => {
    if (!hasBusinessPlan || !voiceEnabled) return;
    setLoadingVoices(true);
    try {
      const response = await get(`/agents/default-voices`);
      const payload = response?.data ?? [];
      const normalized = Array.isArray(payload?.voices)
        ? payload.voices
        : Array.isArray(payload)
          ? payload
          : [];
      const mapped: VoiceOption[] = normalized.map((v: any) => ({
        id: v.id || v.voice_id || v.voiceId,
        name: v.name || v.display_name || 'Unnamed voice',
        description: v.description,
        preview_url: v.preview_url || v.previewUrl,
      })).filter((v: VoiceOption) => v.id);
      setVoices(mapped);
    } catch (error: any) {
      console.error('Error loading voices', error);
      toast.error('Unable to load default voices yet.', toastConfig);
    } finally {
      setLoadingVoices(false);
    }
  };

  useEffect(() => {
    fetchVoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voiceEnabled, hasBusinessPlan]);

  const handleToggle = (value: boolean) => {
    if (!hasBusinessPlan) {
      setUpgradeModalVisible(true);
      return;
    }
    setVoiceEnabled(value);
    if (value) {
      fetchVoices();
    }
    setNeedsUpdate(true);
    
    // Track voice mode toggle
    captureEvent('voice_mode_toggled', {
      agent_id: agId,
      voice_enabled: value,
    });
  };

  const handleSelect = (voice: VoiceOption) => {
    setSelectedVoice(voice);
    setNeedsUpdate(true);
    
    // Track voice selection
    captureEvent('voice_selected', {
      agent_id: agId,
      voice_id: voice.id,
      voice_name: voice.name,
    });
  };

  const handlePreview = async (voice: VoiceOption) => {
    if (!hasBusinessPlan) {
      setUpgradeModalVisible(true);
      return;
    }
    if (!agId) {
      toast.info('Save the agent first to preview voices.', toastConfig);
      return;
    }
    setPreviewing(voice.id);
    try {
      const response = await postStream(`/agents/voice-preview`, {
        voice_id: voice.id,
        text: FALLBACK_PREVIEW_TEXT,
      });

      if (response?.stream) {
        const audioBlob = await new Response(response.stream).blob();
        const audioUrl = URL.createObjectURL(audioBlob);
        const audio = new Audio(audioUrl);
        audio.play().catch((err) => {
          console.error('Error playing audio preview', err);
          toast.error('Unable to play the audio preview.', toastConfig);
        });
        audio.onended = () => URL.revokeObjectURL(audioUrl);
      } else {
        toast.error('Preview audio was not returned.', toastConfig);
      }
    } catch (error: any) {
      console.error('Error requesting voice preview', error);
      toast.error('Unable to generate a preview yet.', toastConfig);
    } finally {
      setPreviewing(null);
    }
  };

  const renderVoiceList = () => {
    if (!voiceEnabled) {
      return (
        <p className="text-sm text-muted-foreground">
          Enable voice to choose a voice for your agent.
        </p>
      );
    }

    if (loadingVoices) {
      return (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading voices...
        </div>
      );
    }

    if (!voices.length) {
      return (
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            No voices available yet. Try refreshing after backend is ready.
          </p>
          <Button variant="outline" onClick={fetchVoices} size="sm">
            Refresh voices
          </Button>
        </div>
      );
    }

    return (
      <div className="overflow-hidden rounded-lg border bg-card divide-y">
        {voices.map((voice) => {
          const isSelected = selectedVoice?.id === voice.id;
          return (
            <div key={voice.id} className={isSelected ? 'bg-muted/20 p-3' : 'p-3'}>
              <div className="flex flex-wrap items-center gap-3">
                <Mic className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1 basis-40">
                  <div className="flex items-center gap-2">
                    <p className="truncate text-sm font-medium">{voice.name}</p>
                    {isSelected && (
                      <span className="text-[10px] font-medium text-muted-foreground">Selected</span>
                    )}
                  </div>
                  {voice.description && (
                    <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                      {voice.description}
                    </p>
                  )}
                </div>
                <div className="ml-auto flex shrink-0 gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handlePreview(voice)}
                    disabled={previewing === voice.id}
                  >
                    {previewing === voice.id ? (
                      <>
                        <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Play className="mr-1.5 h-3.5 w-3.5" />
                        Preview
                      </>
                    )}
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleSelect(voice)}
                    variant={isSelected ? 'default' : 'outline'}
                  >
                    <Volume2 className="mr-1.5 h-3.5 w-3.5" />
                    {isSelected ? 'Selected' : 'Select'}
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Keep the premium state explicit without rendering a decorative fake preview.
  if (!hasBusinessPlan) {
    return (
      <div className="w-full">
        <AgentTabHeader
          icon={Mic}
          title="Voice"
          description="Choose how your agent listens and responds with speech."
        />
        <div className="mx-auto w-full max-w-4xl py-6">
          <div className="rounded-lg border bg-card px-6 py-10 text-center">
            <Lock className="mx-auto h-5 w-5 text-muted-foreground" />
            <h3 className="mt-3 text-sm font-semibold">Voice requires Convert</h3>
            <p className="mx-auto mt-1 max-w-md text-xs leading-5 text-muted-foreground">
              Give your agent a voice and engage visitors with natural speech.
            </p>
            <Button size="sm" className="mt-4" onClick={() => setUpgradeModalVisible(true)}>
              Upgrade to Convert
            </Button>
          </div>
        </div>

        <Upgrade
          showModal={upgradeModalVisible}
          setShowModal={setUpgradeModalVisible}
          sessionId={null}
        />
      </div>
    );
  }

  return (
    <div className="w-full">
      <AgentTabHeader
        icon={Mic}
        title="Voice"
        description="Choose how your agent listens and responds with speech."
      />
      <div className="mx-auto w-full max-w-4xl space-y-8 py-6">
        <section className="space-y-3">
          <div>
            <h3 className="text-sm font-semibold">Voice mode</h3>
            <p className="mt-1 text-xs text-muted-foreground">
              Enable spoken conversations for this agent.
            </p>
          </div>
          <div className="rounded-lg border bg-card p-4">
            <div className="flex items-center justify-between gap-6">
              <div>
                <Label className="text-sm">Enable voice</Label>
                <p className="text-xs text-muted-foreground">
                Toggle to capture visitor audio and respond with speech.
              </p>
              </div>
            <Switch
              checked={voiceEnabled}
              onCheckedChange={handleToggle}
            />
            </div>
          </div>
        </section>

        <section className={voiceEnabled ? 'space-y-3' : 'pointer-events-none space-y-3 opacity-60'}>
          <div>
            <h3 className="text-sm font-semibold">Default voice</h3>
            <p className="mt-1 text-xs text-muted-foreground">
              Preview and select an ElevenLabs voice.
            </p>
          </div>
            {renderVoiceList()}
        </section>
      </div>

      <Upgrade
        showModal={upgradeModalVisible}
        setShowModal={setUpgradeModalVisible}
        sessionId={null}
      />
    </div>
  );
}

